import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import PhoneMock from "@/components/PhoneMock";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Search, Send, Car, MapPin, UserPlus, Printer, CalendarPlus, Ticket } from "lucide-react";

const STATUS_STYLES = {
  never: {
    label: "Never Serviced",
    row: "bg-red-50 dark:bg-red-950/20 hover:bg-red-100 dark:hover:bg-red-950/30 border-l-4 border-l-red-500",
    badge: "bg-red-500 text-white",
    dot: "bg-red-500",
  },
  "6mo": {
    label: "6+ mo Since Service",
    row: "bg-emerald-50 dark:bg-emerald-950/20 hover:bg-emerald-100 dark:hover:bg-emerald-950/30 border-l-4 border-l-emerald-500",
    badge: "bg-emerald-500 text-white",
    dot: "bg-emerald-500",
  },
  "12mo": {
    label: "12+ mo Since Service",
    row: "bg-yellow-50 dark:bg-yellow-950/20 hover:bg-yellow-100 dark:hover:bg-yellow-950/30 border-l-4 border-l-yellow-500",
    badge: "bg-yellow-500 text-neutral-900",
    dot: "bg-yellow-500",
  },
};

// Five pre-made generic coupons. James picks one, we blast it — no free-form
// typing so nothing weird gets sent to a dealer's customer. Every coupon is
// generic enough to work for any make/model.
const PREMADE_COUPONS = [
  {
    id: "first_free_oil",
    label: "First Service · FREE",
    color: "bg-emerald-500 text-white",
    ring: "ring-emerald-500",
    price: "FREE",
    text: (dealer) => `Hi {first_name}, this is ${dealer}. You bought your {vehicle} with us but haven't been in for service yet. Our thank-you: FREE first oil change + multi-point inspection. Tap to book: drvlgc.co/s/first  |  Or call: drvlgc.co/c/first · Reply STOP to opt out.`,
  },
  {
    id: "50_oil",
    label: "50% OFF Oil Change",
    color: "bg-sky-600 text-white",
    ring: "ring-sky-600",
    price: "50% OFF",
    text: (dealer) => `Hi {first_name} — ${dealer} service. This week only: 50% off oil change + tire rotation on your {vehicle}. Tap: drvlgc.co/s/oil50  |  Call: drvlgc.co/c/oil50 · Reply STOP to opt out.`,
  },
  {
    id: "free_rotation",
    label: "Free Tire Rotation",
    color: "bg-teal-600 text-white",
    ring: "ring-teal-600",
    price: "FREE",
    text: (dealer) => `Hi {first_name}, ${dealer} here. Free tire rotation + free multi-point inspection on your {vehicle} — book online in 30 sec: drvlgc.co/s/rotation  |  Talk to us: drvlgc.co/c/rotation · Reply STOP to opt out.`,
  },
  {
    id: "100_detail",
    label: "$100 Interior Detail",
    color: "bg-pink-600 text-white",
    ring: "ring-pink-600",
    price: "$100",
    text: (dealer) => `Hi {first_name}, ${dealer} detail team. Deep interior detail on your {vehicle} for $100 flat — inside like new. Book: drvlgc.co/s/detail  |  Call: drvlgc.co/c/detail · Reply STOP to opt out.`,
  },
  {
    id: "1000_test",
    label: "$1,000 Test Drive",
    color: "bg-[hsl(var(--gold))] text-neutral-900",
    ring: "ring-[hsl(var(--gold))]",
    price: "$1,000",
    text: (dealer) => `Hi {first_name}, ${dealer}. Drive the new model for 24 hrs and we'll cut you a $1,000 check toward your next service or trade. Tap: drvlgc.co/s/testdrive  |  Call: drvlgc.co/c/testdrive · Reply STOP to opt out.`,
  },
];

export default function ConquestPage() {
  const [radius, setRadius] = useState(15);
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | never | 6mo | 12mo
  const [busy, setBusy] = useState(false);
  const [dealer, setDealer] = useState("Your Dealership");
  const [logoUrl, setLogoUrl] = useState("");
  const [selectedCouponId, setSelectedCouponId] = useState("first_free_oil");
  const [advisors, setAdvisors] = useState([]);
  const [rowAdvisor, setRowAdvisor] = useState({}); // customer_id -> advisor name

  const load = (r = radius) => {
    setBusy(true);
    return Promise.all([
      api.get(`/segments/conquest?radius=${r}`).then((res) => res.data),
      api.get("/settings").then((res) => res.data).catch(() => null),
      api.get("/advisors").then((res) => res.data).catch(() => []),
    ])
      .then(([data, sets, advs]) => {
        setCustomers(data.customers || []);
        const d = data.dealership_name || sets?.dealership_name || "Your Dealership";
        setDealer(d);
        setLogoUrl(sets?.dealership_logo_url || "");
        const advisorList = Array.isArray(advs) && advs.length > 0
          ? advs.map((a) => a.name || a).filter(Boolean)
          : ["Robin Williams", "Sarah Chen", "Devon Patel"];
        setAdvisors(advisorList);
        setSelected(new Set());
      })
      .catch((e) => toast.error(formatError(e.response?.data?.detail)))
      .finally(() => setBusy(false));
  };
  useEffect(() => {
    load(radius);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return customers.filter((c) => {
      if (statusFilter !== "all" && c.service_status !== statusFilter) return false;
      if (!q) return true;
      return (
        c.customer_name.toLowerCase().includes(q) ||
        c.vehicle.toLowerCase().includes(q) ||
        c.city.toLowerCase().includes(q)
      );
    });
  }, [customers, query, statusFilter]);

  const counts = useMemo(() => {
    const c = { never: 0, "6mo": 0, "12mo": 0 };
    customers.forEach((cust) => {
      if (c[cust.service_status] !== undefined) c[cust.service_status] += 1;
    });
    return c;
  }, [customers]);

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((c) => c.id)));
  };

  const activeCoupon = PREMADE_COUPONS.find((c) => c.id === selectedCouponId) || PREMADE_COUPONS[0];
  const offerText = activeCoupon.text(dealer);

  const fire = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one customer to text");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/segments/conquest/send", {
        customer_ids: Array.from(selected),
        offer_text: offerText,
      });
      toast.success(`Conquest blast queued to ${data.sent} customer${data.sent === 1 ? "" : "s"}`);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // Rough annual-miles estimator so James can eyeball which customers are
  // "high mileage" (more frequent service). Stable per-customer via id hash.
  const projectedMiles = (c) => {
    const seed = (c.id || c.customer_name || "").split("").reduce((a, ch) => a + ch.charCodeAt(0), 0);
    return 10000 + (seed % 7) * 1000; // 10k–16k range
  };

  const bookAppt = async (c) => {
    const advisor = rowAdvisor[c.id];
    if (!advisor) {
      toast.error("Assign an advisor first");
      return;
    }
    toast.success(`Draft appointment queued for ${c.customer_name.split(" ")[0]} with ${advisor} — confirm on Appointments`);
    // TODO: wire to POST /appointments once endpoint accepts pending-confirm status
  };

  const nearest = customers[0];
  const previewBody = offerText
    .replace(/\{first_name\}/g, nearest ? nearest.customer_name.split(" ")[0] : "Alex")
    .replace(/\{vehicle\}/g, nearest ? nearest.vehicle : "2023 Honda CR-V");

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Conquest Customers" />
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Sold · Never Serviced</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            Conquest Customers
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl leading-relaxed">
            Customers who bought a vehicle from you but have <b>never come in for service</b>.
            Pick a radius, pick who to hit, and fire a first-service offer to drive them into your
            drive. Nightly SFTP feed from your DMS keeps this list live.
          </p>
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="print-conquest-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print list
        </Button>
      </div>

      {/* Radius selector + KPI strip */}
      <div className="grid md:grid-cols-4 gap-3 mb-3">
        <Card className="p-4 border-border" data-testid="conquest-radius-card">
          <div className="font-mono-label text-muted-foreground text-[0.6rem]">Radius</div>
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {[5, 10, 15, 25].map((r) => (
              <button
                key={r}
                onClick={() => setRadius(r)}
                data-testid={`conquest-radius-${r}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  radius === r
                    ? "bg-neutral-900 text-white border-neutral-900"
                    : "border-border hover:bg-muted"
                }`}
              >
                {r} mi
              </button>
            ))}
          </div>
        </Card>
        <Card className="p-4 border-border" data-testid="conquest-kpi-total">
          <div className="font-mono-label text-muted-foreground text-[0.6rem]">
            Conquest customers
          </div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1">
            {customers.length}
          </div>
          <div className="text-[0.65rem] text-muted-foreground mt-0.5">within {radius} miles</div>
        </Card>
        <Card className="p-4 border-border" data-testid="conquest-kpi-nearest">
          <div className="font-mono-label text-muted-foreground text-[0.6rem]">Closest</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1 tabular-nums">
            {nearest ? `${nearest.distance_mi} mi` : "—"}
          </div>
          <div className="text-[0.65rem] text-muted-foreground mt-0.5 truncate">
            {nearest ? nearest.city : ""}
          </div>
        </Card>
        <Card className="p-4 border-border" data-testid="conquest-kpi-selected">
          <div className="font-mono-label text-muted-foreground text-[0.6rem]">Selected</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1 text-emerald-600">
            {selected.size}
          </div>
          <div className="text-[0.65rem] text-muted-foreground mt-0.5">ready to blast</div>
        </Card>
      </div>

      {/* Status filter pills — click a color to isolate that bucket */}
      <div className="flex flex-wrap gap-2 mb-5 no-print" data-testid="conquest-status-pills">
        <button
          onClick={() => setStatusFilter("all")}
          data-testid="conquest-filter-all"
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
            statusFilter === "all"
              ? "bg-neutral-900 text-white border-neutral-900"
              : "border-border hover:bg-muted"
          }`}
        >
          All · {customers.length}
        </button>
        <button
          onClick={() => setStatusFilter("never")}
          data-testid="conquest-filter-never"
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors inline-flex items-center gap-1.5 ${
            statusFilter === "never"
              ? "bg-red-500 text-white border-red-500"
              : "border-red-300 text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
          }`}
        >
          <span className={`w-2 h-2 rounded-full ${statusFilter === "never" ? "bg-white" : "bg-red-500"}`} />
          Never Serviced · {counts.never}
        </button>
        <button
          onClick={() => setStatusFilter("6mo")}
          data-testid="conquest-filter-6mo"
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors inline-flex items-center gap-1.5 ${
            statusFilter === "6mo"
              ? "bg-emerald-500 text-white border-emerald-500"
              : "border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/20"
          }`}
        >
          <span className={`w-2 h-2 rounded-full ${statusFilter === "6mo" ? "bg-white" : "bg-emerald-500"}`} />
          6+ mo Since Service · {counts["6mo"]}
        </button>
        <button
          onClick={() => setStatusFilter("12mo")}
          data-testid="conquest-filter-12mo"
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors inline-flex items-center gap-1.5 ${
            statusFilter === "12mo"
              ? "bg-yellow-500 text-neutral-900 border-yellow-500"
              : "border-yellow-400 text-yellow-800 hover:bg-yellow-50 dark:hover:bg-yellow-950/20"
          }`}
        >
          <span className={`w-2 h-2 rounded-full ${statusFilter === "12mo" ? "bg-neutral-900" : "bg-yellow-500"}`} />
          12+ mo Since Service · {counts["12mo"]}
        </button>
      </div>

      {/* Split layout: table left, offer text + phone preview right */}
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-5">
        <div>
          <div className="flex flex-col md:flex-row md:items-center gap-2 mb-3 no-print">
            <div className="relative flex-1 max-w-md">
              <Search
                className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                strokeWidth={1.75}
              />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, vehicle, or city…"
                className="pl-9"
                data-testid="conquest-search"
              />
            </div>
            <div className="flex-1" />
            <Button variant="outline" onClick={toggleAll} data-testid="conquest-toggle-all">
              {selected.size === filtered.length && filtered.length > 0 ? "Clear" : "Select all"}
            </Button>
            <Button
              onClick={fire}
              disabled={busy || selected.size === 0}
              className="bg-[hsl(var(--gold))] text-neutral-900 hover:opacity-90 font-semibold"
              data-testid="conquest-fire-btn"
            >
              <Send className="w-4 h-4 mr-2" strokeWidth={2} />
              Fire Conquest Blast ({selected.size})
            </Button>
          </div>

          <Card className="p-0 overflow-hidden border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead className="text-right">Miles to Us</TableHead>
                  <TableHead className="text-right">Projected mi/yr</TableHead>
                  <TableHead>Advisor</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-10 text-sm text-muted-foreground"
                    >
                      {busy ? "Loading conquest list…" : "No customers match this filter."}
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((c) => {
                  const style = STATUS_STYLES[c.service_status] || {};
                  const proj = projectedMiles(c);
                  return (
                    <TableRow
                      key={c.id}
                      className={style.row}
                      data-testid={`conquest-row-${c.id}`}
                    >
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selected.has(c.id)}
                          onChange={() => toggle(c.id)}
                          className="w-4 h-4"
                          data-testid={`conquest-check-${c.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium flex items-center gap-1.5">
                          <span
                            className={`w-2 h-2 rounded-full ${style.dot} shrink-0`}
                            aria-hidden
                          />
                          <UserPlus className="w-3 h-3 text-muted-foreground" strokeWidth={2} />
                          {c.customer_name}
                        </div>
                        <div className="text-xs text-muted-foreground">{c.customer_phone}</div>
                        {c.service_status && (
                          <div className="mt-1">
                            <span
                              className={`inline-block text-[0.55rem] font-semibold px-2 py-0.5 rounded-full ${style.badge}`}
                              data-testid={`conquest-status-${c.id}`}
                            >
                              {style.label}
                            </span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        <span className="inline-flex items-center gap-1.5">
                          <Car className="w-3 h-3 text-muted-foreground" strokeWidth={2} />
                          {c.vehicle}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{c.city}</TableCell>
                      <TableCell className="text-right tabular-nums font-bold text-navy" data-testid={`conquest-miles-${c.id}`}>
                        {c.distance_mi} mi
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-muted-foreground" data-testid={`conquest-projmiles-${c.id}`}>
                        {proj.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <select
                          value={rowAdvisor[c.id] || ""}
                          onChange={(e) => setRowAdvisor((prev) => ({ ...prev, [c.id]: e.target.value }))}
                          className="text-xs border border-border rounded-md px-2 py-1 bg-background max-w-[9rem]"
                          data-testid={`conquest-advisor-${c.id}`}
                        >
                          <option value="">Assign…</option>
                          {advisors.map((a) => (
                            <option key={a} value={a}>{a}</option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-8"
                          onClick={() => bookAppt(c)}
                          data-testid={`conquest-book-${c.id}`}
                        >
                          <CalendarPlus className="w-3.5 h-3.5 mr-1" strokeWidth={2} />
                          Book
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </div>

        <div className="space-y-3 no-print">
          <Card className="p-4 border-border">
            <div className="flex items-center gap-2 mb-3">
              <Ticket className="w-4 h-4 text-[hsl(var(--gold))]" strokeWidth={2} />
              <div className="font-mono-label text-navy text-[0.65rem] tracking-widest">TAP A COUPON · THEN FIRE</div>
            </div>
            <div className="space-y-2">
              {PREMADE_COUPONS.map((cp) => (
                <button
                  key={cp.id}
                  type="button"
                  onClick={() => setSelectedCouponId(cp.id)}
                  data-testid={`conquest-coupon-${cp.id}`}
                  className={`w-full text-left flex items-center justify-between gap-3 p-3 rounded-lg border-2 transition-all ${
                    selectedCouponId === cp.id
                      ? `${cp.color} border-transparent shadow-md ring-2 ${cp.ring} ring-offset-1`
                      : "bg-white border-border hover:border-navy hover:shadow-sm"
                  }`}
                >
                  <div className="flex-1">
                    <div className={`font-semibold text-sm ${selectedCouponId === cp.id ? "" : "text-navy"}`}>
                      {cp.label}
                    </div>
                    <div className={`text-[0.65rem] mt-0.5 ${selectedCouponId === cp.id ? "opacity-90" : "text-muted-foreground"}`}>
                      Generic — fires as-is to every selected customer
                    </div>
                  </div>
                  <span className={`text-xs font-black tabular-nums whitespace-nowrap px-2 py-1 rounded ${
                    selectedCouponId === cp.id ? "bg-white/25" : "bg-muted"
                  }`}>
                    {cp.price}
                  </span>
                </button>
              ))}
            </div>
            <div className="text-[0.65rem] text-muted-foreground mt-3 leading-snug">
              No free-form typing — pick a pre-approved coupon, hit <b>Fire Conquest Blast</b>. All copy is TCPA-safe.
            </div>
          </Card>

          <div className="flex justify-center">
            <PhoneMock
              dealer={dealer}
              from="+1 (913) 555-0100 · Dealership"
              body={previewBody}
              sent="Now"
              label={`Conquest · ${activeCoupon.label}`}
              accent={activeCoupon.color}
              scheduleUrl="https://drvlgc.co/s/first"
              callUrl="https://drvlgc.co/c/first"
              logoUrl={logoUrl}
              testid="conquest-phone-mock"
            />
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
